import { MapPin, LogIn, LogOut, Clock, Check, Pause, AlertCircle } from "lucide-react";
import "../../../css/OnRoad.css";

const iconByEvent = {
    LLEGADA: MapPin,
    ENTRADA: LogIn,
    SALIDA: LogOut
};

// Función para generar títulos dinámicos basados en la fase
const getTitleByEventAndPhase = (evento, fase) => {
    const faseLabel = fase === 'CARGUE' ? 'Cargue' : 'Descargue';
    
    const baseTitles = {
        LLEGADA: { primary: 'Llegada', secondary: faseLabel },
        ENTRADA: { primary: 'Entrada', secondary: faseLabel },
        SALIDA: { primary: 'Salida', secondary: faseLabel }
    };
    
    return baseTitles[evento] || { primary: evento, secondary: faseLabel };
};

const subtitleByEvent = {
    LLEGADA: "Llegada al punto",
    ENTRADA: "Entrada a instalaciones",
    SALIDA: "Salida de instalaciones"
};

export default function EventCard({ 
    evento, 
    estadoDetallado, 
    onClick, 
    disabled = false,
    gridPosition,
    fase = 'CARGUE' 
}) {
    const Icon = iconByEvent[evento] || Clock;
    const titleParts = getTitleByEventAndPhase(evento, fase);
    const subtitle = subtitleByEvent[evento] || "Evento";

    const { 
        estado, 
        esMixto, 
        gruposReportados = [], 
        gruposPendientes = [] 
    } = estadoDetallado || { estado: 'BLOQUEADO' };
    
    const formatTime = (ts) => {
        if (!ts) return null;
        try {
            return new Date(ts).toLocaleTimeString('es-CO', { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        } catch {
            return null;
        }
    };

    const handleClick = () => {
        if (!disabled && estado === 'PENDIENTE' && onClick) {
            onClick();
        }
    };

    const getStatusBadge = () => {
        if (esMixto) {
            return (
                <div className="status-badge mixed">
                    <div className="mixed-indicator">
                        <span className="mixed-progress">
                            {gruposReportados.length}/{gruposReportados.length + gruposPendientes.length}
                        </span>
                    </div>
                </div>
            );
        }

        switch (estado) {
            case 'REPORTADO':
                return (
                    <div className="status-badge reported">
                        <Check size={12} />
                    </div>
                );
            case 'PENDIENTE':
                return (
                    <div className="status-badge pending">
                        <AlertCircle size={12} />
                    </div>
                );
            case 'BLOQUEADO':
                return (
                    <div className="status-badge blocked">
                        <Pause size={12} />
                    </div>
                );
            default:
                return null;
        }
    };

    // Tooltip para estado mixto
    const getTooltipText = () => {
        if (!esMixto) return null;

        const reportados = gruposReportados
            .map(g => `✅ ${g.origen} → ${g.destino}`)
            .join('\n');
        
        const pendientes = gruposPendientes
            .map(g => `⏳ ${g.origen} → ${g.destino}`)
            .join('\n');

        return `Reportados:\n${reportados}\n\nPendientes:\n${pendientes}`;
    };

    return (
        <div 
            className={`event-card-modern ${estado.toLowerCase()} ${
                esMixto ? 'mixed' : ''
            } ${disabled ? 'disabled' : ''}`}
            onClick={handleClick}
            style={{ '--grid-position': gridPosition }}
            title={getTooltipText()}
        >
            {getStatusBadge()}
            
            <div className="card-content">
                <div className="card-header">
                    <div className={`event-icon ${estado.toLowerCase()}`}>
                        <Icon size={22} />
                    </div>
                    <div className="event-details">
                        <h3 className="event-title">
                            <span className="title-primary">{titleParts.primary}</span>
                            <span className="title-secondary">{titleParts.secondary}</span>
                        </h3>
                        <p className="event-subtitle">{subtitle}</p>
                        {/* Mostrar información de grupos en estado mixto */}
                        {esMixto && (
                            <div className="event-mixed-info">
                                <span className="mixed-text">
                                    {gruposPendientes.length} grupo{gruposPendientes.length !== 1 ? 's' : ''} pendiente{gruposPendientes.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        )}
                        
                        {/* Mostrar timestamp del primer grupo reportado */}
                        {gruposReportados.length > 0 && (
                            <div className="event-timestamp">
                                {formatTime(gruposReportados[0].timestamp)}
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="card-actions">
                    <button 
                        className={`action-button ${estado.toLowerCase()}`}
                        disabled={disabled || estado !== 'PENDIENTE'}
                    >
                        {estado === 'REPORTADO' ? 'Completado' : 'Reportar'}
                    </button>
                </div>
            </div>
        </div>
    );
}